import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import { ActionsPausedContext, SheetNoticeContext } from '@didto/ui';
import { Shell } from './shell';
import { SheetConnectionNotice } from './connection';
import { useOnline } from '@/lib/network';
import { RESET_PASSWORD_PATH } from '@/lib/passwordRecovery';
import { DeviceRuntime } from './deviceRuntime';
import { Outlet } from 'react-router-dom';
import { RequireDeviceSession, RequireHuman, RequirePermission } from './guards';
import { LoginPage } from '@/routes/auth/LoginPage';
import { EnrollPage } from '@/routes/auth/EnrollPage';
import { LockPage } from '@/routes/auth/LockPage';
import { MfaPage } from '@/routes/auth/MfaPage';
import { ResetPasswordPage } from '@/routes/auth/ResetPasswordPage';
import { DashboardPage } from '@/routes/DashboardPage';
import { PlaceholderPage } from '@/routes/PlaceholderPage';
import { PosPage } from '@/routes/pos/PosPage';
import { KdsPage } from '@/routes/kds/KdsPage';
import { ExpoPage } from '@/routes/expo/ExpoPage';
import { OrdersPage } from '@/routes/orders/OrdersPage';
import { TablesPage } from '@/routes/tables/TablesPage';
import { CatalogPage } from '@/routes/catalog/CatalogPage';
import { MenuImportPage } from '@/routes/catalog/MenuImportPage';
import { ModifierGroupsPage } from '@/routes/catalog/ModifierGroupsPage';
import { EmployeesPage } from '@/routes/employees/EmployeesPage';
import { DevicesPage } from '@/routes/devices/DevicesPage';
import { SettingsPage } from '@/routes/settings/SettingsPage';
import { RolesPage } from '@/routes/settings/RolesPage';
import { StationsPage } from '@/routes/settings/StationsPage';
import { PaymentMethodsPage } from '@/routes/settings/PaymentMethodsPage';
import { SecurityPage } from '@/routes/settings/SecurityPage';
import { AuditPage } from '@/routes/settings/AuditPage';
import { OwnerReviewGate } from './ownerReview';
import { ShiftsPage } from '@/routes/shifts/ShiftsPage';
import { ReviewQueuePage } from '@/routes/shifts/ReviewQueuePage';
import { DayPage } from '@/routes/shifts/DayPage';
import { ShiftDetailPage } from '@/routes/shifts/ShiftDetailPage';
import { ApprovalsPage } from '@/routes/approvals/ApprovalsPage';
import { ReportsPage } from '@/routes/reports/ReportsPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { useSession } from './session';

function Root() {
  const online = useOnline();
  const s = useSession();
  const loc = useLocation();
  // ADR-041: a password reset link opens the reset page wherever it landed (the Site URL sends it
  // to "/"), and the session it opened stays there until the password is changed or the reset is
  // cancelled. A link that failed is explained there too.
  if ((s.recovery || s.linkError) && loc.pathname !== RESET_PASSWORD_PATH)
    return <Navigate to={RESET_PASSWORD_PATH} replace />;
  return (
    <SheetNoticeContext.Provider value={<SheetConnectionNotice />}>
      <ActionsPausedContext.Provider value={!online}>
        <DeviceRuntime />
        <Outlet />
      </ActionsPausedContext.Provider>
    </SheetNoticeContext.Provider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/mfa', element: <MfaPage /> },
      { path: RESET_PASSWORD_PATH, element: <ResetPasswordPage /> },
      { path: '/enroll', element: <EnrollPage /> },
      { path: '/lock', element: <LockPage /> },
      {
        path: '/',
        element: <Shell />,
        children: [
          {
            index: true,
            element: (
              <RequireHuman>
                <DashboardPage />
              </RequireHuman>
            ),
          },
          {
            path: 'pos',
            element: (
              <RequireDeviceSession permission="pos.access">
                <PosPage />
              </RequireDeviceSession>
            ),
          },
          {
            path: 'orders',
            element: (
              <RequirePermission permission="orders.view">
                <OrdersPage />
              </RequirePermission>
            ),
          },
          {
            path: 'kitchen',
            element: (
              <RequireDeviceSession permission="kds.access">
                {/* keyed per tab: switching Kitchen and Bar starts the page afresh (ADR-045) */}
                <KdsPage key="KITCHEN" route="KITCHEN" />
              </RequireDeviceSession>
            ),
          },
          {
            path: 'bar',
            element: (
              <RequireDeviceSession permission="kds.access">
                <KdsPage key="BAR" route="BAR" />
              </RequireDeviceSession>
            ),
          },
          {
            path: 'expo',
            element: (
              <RequireDeviceSession permission="expo.access">
                <ExpoPage />
              </RequireDeviceSession>
            ),
          },
          {
            path: 'tables',
            element: (
              <RequirePermission permission="table.view">
                <TablesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'catalog',
            element: (
              <RequirePermission permission="catalog.view">
                <CatalogPage />
              </RequirePermission>
            ),
          },
          {
            path: 'catalog/import',
            element: (
              <RequireHuman>
                <RequirePermission permission="catalog.manage">
                  <MenuImportPage />
                </RequirePermission>
              </RequireHuman>
            ),
          },
          {
            path: 'catalog/modifiers',
            element: (
              <RequirePermission permission="catalog.view">
                <ModifierGroupsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'employees',
            element: (
              <RequirePermission permission="employee.view">
                <EmployeesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'devices',
            element: (
              <RequirePermission permission="device.view">
                <DevicesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'shifts',
            element: (
              <OwnerReviewGate>
                <ShiftsPage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'shifts/review',
            element: (
              <OwnerReviewGate>
                <ReviewQueuePage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'shifts/day/:date?',
            element: (
              <OwnerReviewGate>
                <DayPage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'shifts/:id',
            element: (
              <OwnerReviewGate>
                <ShiftDetailPage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'approvals',
            element: (
              <OwnerReviewGate>
                <ApprovalsPage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'reports',
            element: (
              <OwnerReviewGate permission="report.money">
                <ReportsPage />
              </OwnerReviewGate>
            ),
          },
          {
            path: 'study-pods',
            element: (
              <RequireHuman>
                <PlaceholderPage
                  module="Study Pods"
                  phase="Phase 12"
                  detail="Pods, pod types, bookings, occupancy and cleaning states."
                />
              </RequireHuman>
            ),
          },
          {
            path: 'settings',
            element: (
              <RequirePermission permission="settings.view">
                <SettingsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'settings/roles',
            element: (
              <RequirePermission permission="settings.view">
                <RolesPage />
              </RequirePermission>
            ),
          },
          {
            path: 'settings/stations',
            element: (
              <RequirePermission permission="station.view">
                <StationsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'settings/payment-methods',
            element: (
              <RequirePermission permission="settings.view">
                <PaymentMethodsPage />
              </RequirePermission>
            ),
          },
          {
            path: 'settings/security',
            element: (
              <RequireHuman>
                <SecurityPage />
              </RequireHuman>
            ),
          },
          {
            path: 'settings/audit',
            element: (
              <RequirePermission permission="audit.view">
                <AuditPage />
              </RequirePermission>
            ),
          },
          { path: 'inventory', element: <Navigate to="/" replace /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
